import React from 'react';
import { useCurrentFrame, useVideoConfig, interpolate, spring, AbsoluteFill } from 'remotion';
import { COLORS, SUBS, SCENES } from '../constants';
import { GlowBackground } from '../components/GlowBackground';
import { AnimatedText } from '../components/AnimatedText';
import { SyncedSubtitles } from '../components/SyncedSubtitles';

/* ── Quote Builder Mockup ── */
const QuoteMockup: React.FC<{ delay: number }> = ({ delay }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const f = frame - delay;
  if (f < 0) return null;

  const scale = spring({ frame: f, fps, config: { damping: 14, stiffness: 100 } });
  const opacity = interpolate(f, [0, 15], [0, 1], { extrapolateRight: 'clamp' });

  const rows = [
    { product: 'Servidor Dell R750', qty: 3, price: '$4,200', total: '$12,600' },
    { product: 'Licencia VMware', qty: 10, price: '$580', total: '$5,800' },
    { product: 'Soporte 24/7', qty: 1, price: '$2,400', total: '$2,400' },
  ];

  const rowReveal = (i: number) =>
    interpolate(f, [15 + i * 10, 25 + i * 10], [0, 1], {
      extrapolateRight: 'clamp',
      extrapolateLeft: 'clamp',
    });

  const totalsReveal = interpolate(f, [55, 70], [0, 1], {
    extrapolateRight: 'clamp',
    extrapolateLeft: 'clamp',
  });

  return (
    <div
      style={{
        opacity,
        transform: `scale(${scale})`,
        background: `${COLORS.bgLight}DD`,
        border: `1px solid ${COLORS.primary}25`,
        borderRadius: 16,
        padding: 20,
        flex: 1,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 14,
        }}
      >
        <div
          style={{
            fontSize: 15,
            fontWeight: 700,
            color: COLORS.white,
            fontFamily: 'Inter, system-ui, sans-serif',
          }}
        >
          📋 Cotización #1284
        </div>
        <div
          style={{
            padding: '4px 12px',
            borderRadius: 100,
            background: `${COLORS.primary}20`,
            border: `1px solid ${COLORS.primary}40`,
            fontSize: 10,
            fontWeight: 600,
            color: COLORS.primaryLight,
            fontFamily: 'Inter, system-ui, sans-serif',
          }}
        >
          En Progreso
        </div>
      </div>

      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 12,
          fontFamily: 'Inter, system-ui, sans-serif',
        }}
      >
        <thead>
          <tr>
            {['Producto', 'Cant.', 'P. Unit.', 'Total'].map((h) => (
              <th
                key={h}
                style={{
                  padding: '6px 8px',
                  textAlign: 'left',
                  color: COLORS.gray,
                  fontWeight: 500,
                  borderBottom: `1px solid ${COLORS.grayDark}30`,
                  fontSize: 11,
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ opacity: rowReveal(i) }}>
              <td style={{ padding: '6px 8px', color: COLORS.white }}>{row.product}</td>
              <td style={{ padding: '6px 8px', color: COLORS.gray }}>{row.qty}</td>
              <td style={{ padding: '6px 8px', color: COLORS.gray }}>{row.price}</td>
              <td style={{ padding: '6px 8px', color: COLORS.emerald, fontWeight: 600 }}>
                {row.total}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div
        style={{
          opacity: totalsReveal,
          marginTop: 12,
          borderTop: `1px solid ${COLORS.grayDark}30`,
          paddingTop: 10,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        {[
          { label: 'Subtotal', value: '$20,800' },
          { label: 'Descuento (5%)', value: '-$1,040', color: COLORS.amber },
          { label: 'IVA (13%)', value: '$2,569' },
        ].map((r) => (
          <div
            key={r.label}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 11,
              fontFamily: 'Inter, system-ui, sans-serif',
            }}
          >
            <span style={{ color: COLORS.gray }}>{r.label}</span>
            <span style={{ color: r.color || COLORS.gray, fontWeight: 500 }}>{r.value}</span>
          </div>
        ))}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 16,
            fontWeight: 800,
            fontFamily: 'Inter, system-ui, sans-serif',
            marginTop: 4,
          }}
        >
          <span style={{ color: COLORS.white }}>Total</span>
          <span style={{ color: COLORS.emerald }}>$22,329</span>
        </div>
      </div>
    </div>
  );
};

/* ── Approval Workflow Mockup ── */
const ApprovalMockup: React.FC<{ delay: number }> = ({ delay }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const f = frame - delay;
  if (f < 0) return null;

  const scale = spring({ frame: f, fps, config: { damping: 14, stiffness: 100 } });
  const opacity = interpolate(f, [0, 15], [0, 1], { extrapolateRight: 'clamp' });

  const approvals = [
    {
      client: 'TechCorp SA',
      amount: '$22,329',
      disc: '5%',
      status: 'Pendiente',
      statusColor: COLORS.amber,
    },
    {
      client: 'Industrias Vega',
      amount: '$8,450',
      disc: '12%',
      status: 'Aprobada',
      statusColor: COLORS.emerald,
    },
    {
      client: 'Global Trade',
      amount: '$45,800',
      disc: '18%',
      status: 'Revisión',
      statusColor: COLORS.violet,
    },
  ];

  const rowReveal = (i: number) =>
    interpolate(f, [20 + i * 12, 30 + i * 12], [0, 1], {
      extrapolateRight: 'clamp',
      extrapolateLeft: 'clamp',
    });
  const statsReveal = interpolate(f, [60, 75], [0, 1], {
    extrapolateRight: 'clamp',
    extrapolateLeft: 'clamp',
  });

  return (
    <div
      style={{
        opacity,
        transform: `scale(${scale})`,
        background: `${COLORS.bgLight}DD`,
        border: `1px solid ${COLORS.violet}25`,
        borderRadius: 16,
        padding: 20,
        flex: 1,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 14,
        }}
      >
        <div
          style={{
            fontSize: 15,
            fontWeight: 700,
            color: COLORS.white,
            fontFamily: 'Inter, system-ui, sans-serif',
          }}
        >
          ✅ Aprobaciones
        </div>
        <div
          style={{
            padding: '4px 12px',
            borderRadius: 100,
            background: `${COLORS.amber}15`,
            border: `1px solid ${COLORS.amber}40`,
            fontSize: 10,
            fontWeight: 600,
            color: COLORS.amber,
            fontFamily: 'Inter, system-ui, sans-serif',
          }}
        >
          3 pendientes
        </div>
      </div>

      {approvals.map((a, i) => (
        <div
          key={i}
          style={{
            opacity: rowReveal(i),
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 0',
            borderTop: i > 0 ? `1px solid ${COLORS.grayDark}20` : 'none',
          }}
        >
          <div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: COLORS.white,
                fontFamily: 'Inter, system-ui, sans-serif',
              }}
            >
              {a.client}
            </div>
            <div
              style={{
                fontSize: 10,
                color: COLORS.gray,
                fontFamily: 'Inter, system-ui, sans-serif',
              }}
            >
              {a.amount} · Desc. {a.disc}
            </div>
          </div>
          <div
            style={{
              padding: '3px 10px',
              borderRadius: 100,
              background: `${a.statusColor}15`,
              border: `1px solid ${a.statusColor}40`,
              fontSize: 10,
              fontWeight: 600,
              color: a.statusColor,
              fontFamily: 'Inter, system-ui, sans-serif',
            }}
          >
            {a.status}
          </div>
        </div>
      ))}

      <div style={{ opacity: statsReveal, marginTop: 12, display: 'flex', gap: 12 }}>
        {[
          { label: 'Tiempo prom.', value: '4.2h', color: COLORS.cyan },
          { label: 'Aprobadas hoy', value: '12', color: COLORS.emerald },
          { label: 'Auto-aprobadas', value: '68%', color: COLORS.primaryLight },
        ].map((s) => (
          <div
            key={s.label}
            style={{
              flex: 1,
              background: `${s.color}10`,
              border: `1px solid ${s.color}20`,
              borderRadius: 8,
              padding: '6px 8px',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontSize: 16,
                fontWeight: 800,
                color: s.color,
                fontFamily: 'Inter, system-ui, sans-serif',
              }}
            >
              {s.value}
            </div>
            <div
              style={{
                fontSize: 9,
                color: COLORS.gray,
                fontFamily: 'Inter, system-ui, sans-serif',
              }}
            >
              {s.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Pricing Models Mini ── */
const ModelsMini: React.FC<{ delay: number }> = ({ delay }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const f = frame - delay;
  if (f < 0) return null;

  const scale = spring({ frame: f, fps, config: { damping: 14, stiffness: 100 } });
  const opacity = interpolate(f, [0, 15], [0, 1], { extrapolateRight: 'clamp' });

  const models = [
    { name: 'Cost Plus', formula: 'costo × (1 + margen)', color: COLORS.primary },
    { name: 'Competitivo', formula: 'precio_comp - offset', color: COLORS.cyan },
    { name: 'Value Based', formula: 'percepción × factor', color: COLORS.emerald },
    { name: 'Dinámico', formula: 'demanda × elasticidad', color: COLORS.amber },
  ];

  const modelReveal = (i: number) =>
    interpolate(f, [10 + i * 10, 22 + i * 10], [0, 1], {
      extrapolateRight: 'clamp',
      extrapolateLeft: 'clamp',
    });

  return (
    <div
      style={{
        opacity,
        transform: `scale(${scale})`,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        flex: 0.6,
      }}
    >
      <div
        style={{
          fontSize: 14,
          fontWeight: 700,
          color: COLORS.white,
          fontFamily: 'Inter, system-ui, sans-serif',
          marginBottom: 4,
          textAlign: 'center',
        }}
      >
        🧮 16+ Modelos de Pricing
      </div>
      {models.map((m, i) => (
        <div
          key={m.name}
          style={{
            opacity: modelReveal(i),
            background: `${COLORS.bgLight}DD`,
            border: `1px solid ${m.color}30`,
            borderRadius: 10,
            padding: '10px 14px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: m.color,
              fontFamily: 'Inter, system-ui, sans-serif',
            }}
          >
            {m.name}
          </div>
          <div style={{ fontSize: 10, color: COLORS.grayDark, fontFamily: 'monospace' }}>
            {m.formula}
          </div>
        </div>
      ))}
    </div>
  );
};

const DUR = SCENES.FEATURES.duration;

export const FeaturesScene: React.FC = () => {
  const frame = useCurrentFrame();

  const fadeIn = interpolate(frame, [0, 12], [0, 1], { extrapolateRight: 'clamp' });
  const fadeOut = interpolate(frame, [DUR - 15, DUR], [1, 0], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill style={{ opacity: fadeIn * fadeOut }}>
      <GlowBackground variant="cool" />

      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 60px',
        }}
      >
        <div style={{ marginBottom: 24 }}>
          <AnimatedText
            text="Una Plataforma Completa"
            fontSize={48}
            fontWeight={800}
            mode="scaleIn"
            startFrame={3}
          />
        </div>

        <div style={{ display: 'flex', gap: 20, width: '100%' }}>
          <QuoteMockup delay={15} />
          <ApprovalMockup delay={40} />
          <ModelsMini delay={65} />
        </div>
      </div>

      <SyncedSubtitles lines={SUBS.FEATURES} />
    </AbsoluteFill>
  );
};
